'use client';

import { useCallback, useState } from 'react';
import { usePathname } from 'next/navigation';
import { SearchPaginationKeys } from '@/constants/listing-search-keys';
import {
  DefaultAdults,
  DefaultChildren,
  MaxGuests,
  useSearch,
} from '@/context/listing-search-provider';
import {
  clearGuestSearchParams,
} from '@/utils/guest-search-params';
import { logSearchSubmit } from '@/utils/search-analytics';

import { SearchField } from '@/components/search/fields/search-field';
import { SearchFieldPopover } from '@/components/search/fields/search-field-popover';
import { GuestsIcon } from '@/components/search/icons';
import {
  SearchFieldLayout,
  SearchFieldRadius,
} from '@/components/search/search-config';
import { formatGuestsLabel } from '@/components/search/utils/format-guests-label';
import Stepper from '@/components/ui/stepper';

export type WhoFieldProps = {
  id?: string;
  layout?: SearchFieldLayout;
  radius?: SearchFieldRadius;
  showDivider?: boolean;
  isLastInStack?: boolean;
  shouldSubmit?: boolean;
  submitPath?: string;
  className?: string;
};

export const WhoField = ({
  id = 'filter-guests',
  layout = 'stacked',
  radius = 'segment',
  showDivider = false,
  isLastInStack = false,
  shouldSubmit = false,
  submitPath,
  className,
}: WhoFieldProps) => {
  const pathname = usePathname();
  const {
    adults,
    children,
    setAdults,
    setChildren,
    applySearchParams,
    navigate,
    searchParams,
    guestsInteracted,
    markGuestsInteracted,
    resetGuestsInteracted,
  } = useSearch();
  const [open, setOpen] = useState(false);
  const effectiveAdults = adults ?? DefaultAdults;
  const effectiveChildren = children ?? DefaultChildren;
  const hasGuestValue = guestsInteracted;
  const guestLabel = formatGuestsLabel(effectiveAdults, effectiveChildren);

  const handleApply = useCallback(() => {
    if (shouldSubmit && guestsInteracted) {
      applySearchParams(submitPath, { scroll: false });
    }
  }, [shouldSubmit, guestsInteracted, applySearchParams, submitPath]);

  const handleAdultsChange = (count: number) => {
    markGuestsInteracted();
    setAdults(count);
  };

  const handleChildrenChange = (count: number) => {
    markGuestsInteracted();
    setChildren(count);
  };

  const handleClear = () => {
    setAdults(undefined);
    setChildren(undefined);
    resetGuestsInteracted();
    if (shouldSubmit) {
      const target = submitPath ?? pathname;
      const current = new URLSearchParams(searchParams.toString());
      current.delete(SearchPaginationKeys.OFFSET);
      clearGuestSearchParams(current);
      logSearchSubmit(current);
      navigate(
        `${target}${current.toString() ? `?${current.toString()}` : ''}`,
        { scroll: false }
      );
    }
  };

  return (
    <SearchFieldPopover
      id={`${id}-popover`}
      className={className}
      open={open}
      onOpenChange={setOpen}
      onApply={handleApply}
      onClear={handleClear}
      showFooter={false}
      trigger={
        <SearchField
          id={id}
          label="WHO?"
          value={hasGuestValue ? guestLabel : undefined}
          placeholder="Add guests"
          icon={<GuestsIcon />}
          layout={layout}
          radius={radius}
          showDivider={showDivider}
          isLastInStack={isLastInStack}
          onClear={handleClear}
        />
      }
    >
      <div className="flex w-full flex-col gap-4 p-4">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col">
            <label className="ut-sm font-medium text-gray-900">Adults</label>
            <span className="ut-xs text-gray-7">Age 13+</span>
          </div>
          <Stepper
            id={`stepper-${id}-adults`}
            stepDown={() => handleAdultsChange(Math.max(1, effectiveAdults - 1))}
            stepUp={() => {
              if (effectiveAdults + effectiveChildren < MaxGuests) {
                handleAdultsChange(effectiveAdults + 1);
              }
            }}
            disableDown={effectiveAdults <= 1}
            disableUp={effectiveAdults + effectiveChildren >= MaxGuests}
            value={effectiveAdults.toString()}
          />
        </div>
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col">
            <label className="ut-sm font-medium text-gray-900">Children</label>
            <span className="ut-xs text-gray-7">Age 2-12</span>
          </div>
          <Stepper
            id={`stepper-${id}-children`}
            stepDown={() => handleChildrenChange(Math.max(0, effectiveChildren - 1))}
            stepUp={() => {
              if (effectiveAdults + effectiveChildren < MaxGuests) {
                handleChildrenChange(effectiveChildren + 1);
              }
            }}
            disableDown={effectiveChildren <= 0}
            disableUp={effectiveAdults + effectiveChildren >= MaxGuests}
            value={effectiveChildren.toString()}
          />
        </div>
      </div>
    </SearchFieldPopover>
  );
};
